import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Badge } from './ui/badge'
import { getChangelog, type ChangelogEntry } from '../api'
import { Sparkles, Wrench, Bug, Trash2 } from 'lucide-react'

const SECTION_META: Record<string, { label: string; icon: typeof Sparkles; color: string }> = {
  new: { label: 'Новое', icon: Sparkles, color: 'text-green-500' },
  improved: { label: 'Улучшено', icon: Wrench, color: 'text-blue-500' },
  fixed: { label: 'Исправлено', icon: Bug, color: 'text-orange-500' },
  removed: { label: 'Удалено', icon: Trash2, color: 'text-muted-foreground' },
}

export default function ChangelogModal({ open, onClose, currentVersion }: {
  open: boolean
  onClose: () => void
  currentVersion: string
}) {
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    getChangelog().then(setEntries).catch(() => {}).finally(() => setLoading(false))
  }, [open])

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            История версий
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Загрузка...</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Нет записей</div>
          ) : (
            entries.map((entry, i) => (
              <div key={i} className="space-y-3">
                <div className="flex items-center gap-3">
                  <Badge
                    variant="outline"
                    className={`font-mono font-bold ${
                      entry.version === currentVersion
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : ''
                    }`}
                  >
                    v{entry.version}
                  </Badge>
                  <span className="text-sm font-semibold">{entry.title}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {entry.date}
                  </span>
                </div>

                {entry.sections.map((section, j) => {
                  const meta = SECTION_META[section.type] || SECTION_META.new
                  const Icon = meta.icon
                  return (
                    <div key={j} className="ml-1">
                      <div className={`flex items-center gap-1.5 text-xs font-semibold mb-1.5 ${meta.color}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label}
                      </div>
                      <ul className="space-y-1 ml-5">
                        {section.items.map((item, k) => (
                          <li key={k} className="text-sm text-foreground/80 leading-relaxed flex gap-2">
                            <span className="text-muted-foreground/40 select-none shrink-0">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}

                {i < entries.length - 1 && (
                  <div className="border-t border-border/50 pt-2" />
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
